import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  Box,
  Button,
  FormControl,
  OutlinedInput,
  InputLabel,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Colors from "../../../assets/Variables/ColorsVariables.js";
import * as TextVariables from "../../../assets/Variables/TextVariables.js";
import NotificationModal from "../../../components/Notifications/NotificationModal.js";
import SteeperComponent from "../../../components/Steeper/SteeperComponent.js";

const steps = ["IB / Agent", "Sub Accounts", "Configurations"];

function CreateMAMStepTwoPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [activeStep] = useState(2);
  const [showNotification, setShowNotification] = useState(false);
  const [modalProps, setModalProps] = useState({
    iconType: "",
    title: "",
    message: "",
    onAcceptLabel: undefined,
    onRefuseLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
  });

  const showWarn = (title, message) => {
    setModalProps({
      iconType: "warning",
      title,
      message,
      onAcceptLabel: "Close",
      onRefuseLabel: undefined,
      onAccept: () => setShowNotification(false),
      onRefuse: undefined,
    });
    setShowNotification(true);
  };

  useEffect(() => {
    if (!state?.accountEmail && !state?.ibId) {
      navigate("/requests/create-mam-account");
    }
  }, [state, navigate]);

  const [subAccountInput, setSubAccountInput] = useState("");
  const [subAccounts, setSubAccounts] = useState([]);

  const normalizedInput = useMemo(() => subAccountInput.trim(), [subAccountInput]);

  const canAdd = useMemo(() => {
    if (!normalizedInput) return false;
    if (!/^\d+$/.test(normalizedInput)) return false;
    if (subAccounts.includes(normalizedInput)) return false;
    return true;
  }, [normalizedInput, subAccounts]);

  const handleAdd = () => {
    if (!canAdd) {
      if (!normalizedInput) return;

      if (!/^\d+$/.test(normalizedInput)) {
        showWarn("Invalid sub account", "Sub account must be numeric.");
        return;
      }

      if (subAccounts.includes(normalizedInput)) {
        showWarn("Duplicated sub account", "That sub account was already added.");
        return;
      }

      return;
    }

    setSubAccounts((prev) => [...prev, normalizedInput]);
    setSubAccountInput("");
  };

  const handleRemove = (value) => {
    setSubAccounts((prev) => prev.filter((x) => x !== value));
  };

  const handleBack = () => {
    navigate("/requests/create-mam-account", { state });
  };

  const canContinue = subAccounts.length > 0;

  const handleContinue = () => {
    if (!canContinue) {
      showWarn("Missing sub accounts", "Please add at least one sub account.");
      return;
    }

    navigate("/requests/create-mam-account/step-three", {
      state: {
        ...state,
        subAccounts,
      },
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 4,
        maxWidth: "calc(100vw - 280px)",
        minHeight: "calc(100vh - 100px)",
        marginTop: "0px",
        marginLeft: "10px",
        borderRadius: "12px",
        backgroundColor: "#F9F9F9",
      }}
    >
      <NotificationModal
        open={showNotification}
        onClose={() => setShowNotification(false)}
        iconType={modalProps.iconType}
        title={modalProps.title}
        message={modalProps.message}
        onAcceptLabel={modalProps.onAcceptLabel}
        onRefuseLabel={modalProps.onRefuseLabel}
        onAccept={modalProps.onAccept}
        onRefuse={modalProps.onRefuse}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px",
        }}
      >
        <Button
          onClick={() => navigate("/requests")}
          startIcon={<ArrowBackIosIcon style={{ fontSize: 10 }} />}
          sx={{ textTransform: "none", color: "black", fontWeight: "bold" }}
        >
          Go back
        </Button>
      </Box>

      <Box sx={{ width: "70%", margin: "0 auto", marginTop: "6px" }}>
        <SteeperComponent stepList={steps} stepNumber={activeStep} />
      </Box>

      <Paper
        elevation={2}
        sx={{
          padding: 4,
          maxWidth: "70%",
          minHeight: "360px",
          margin: "18px auto 0 auto",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: TextVariables.Body_3_Bold }}>
              Step 2: Add Sub Accounts
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
          </Box>

          <Box
            sx={{
              backgroundColor: "#FFF",
              border: "1px solid #E5E7EB",
              borderRadius: "14px",
              padding: "14px",
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <FormControl fullWidth>
              <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
                Sub account
              </Typography>

              <OutlinedInput
                value={subAccountInput}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, "");
                  setSubAccountInput(onlyNumbers);
                }}
                placeholder="Add a sub account number"
                sx={{ borderRadius: "12px" }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
            </FormControl>

            <Button
              variant="outlined"
              onClick={handleAdd}
              startIcon={<AddIcon />}
              disabled={!normalizedInput}
              sx={{
                height: 44,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#E5E7EB",
                color: "#111827",
                "&:hover": { borderColor: "#D1D5DB" },
              }}
            >
              Add
            </Button>
          </Box>

          {subAccounts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: TextVariables.Caption_1,
                  color: "#6B7280",
                  mb: 1,
                }}
              >
                Added sub accounts
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "14px",
                  overflow: "hidden",
                }}
              >
                {subAccounts.map((sa, index) => (
                  <Box
                    key={sa}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1.2fr 1fr auto",
                      alignItems: "center",
                      px: 2,
                      py: 1.5,
                      borderBottom:
                        index !== subAccounts.length - 1
                          ? "1px solid #F1F5F9"
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: TextVariables.Caption_1,
                        color: "#6B7280",
                      }}
                    >
                      Sub Account {index + 1}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: TextVariables.Caption_1,
                        color: "#4B5563",
                        textAlign: "left",
                      }}
                    >
                      {sa}
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => handleRemove(sa)}
                      sx={{
                        minWidth: "64px",
                        height: "26px",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor: "#EEF2F7",
                        color: "#6B7280",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#E5E7EB",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              paddingX: 3,
              paddingY: 1.1,
              borderColor: "#111827",
              color: "#111827",
              "&:hover": { borderColor: "#111827" },
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!canContinue}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 800,
              paddingX: 3,
              paddingY: 1.1,
              backgroundColor: canContinue ? Colors.Primary_600 : "#E5E7EB",
              color: canContinue ? "#FFF" : "#6B7280",
              "&:hover": {
                backgroundColor: canContinue ? Colors.Primary_700 : "#E5E7EB",
              },
              "&:disabled": {
                color: "#6B7280",
              },
            }}
          >
            Continue
          </Button>
        </Box>
      </Paper>

      <Box sx={{ marginBottom: "100px" }} />
    </Paper>
  );
}

export default CreateMAMStepTwoPage;